export type CssFeatureCheck = {
  feature: string;
  supported: boolean;
};

export type BrowserSupportResult = {
  supported: boolean;
  unsupportedFeatures: string[];
};

/**
 * Checks CSS features required by Tailwind CSS v4.
 *
 * Required features:
 * - CSS `@property` rule (via CSS.registerProperty) — Chrome 85+, Safari 16.4+, Firefox 128+
 * - CSS `color-mix()` function — Chrome 111+, Safari 16.2+, Firefox 113+
 *
 * Combined, these gate at: Chrome 111+, Safari 16.4+, Firefox 128+
 */
export function checkTailwind4Support(): BrowserSupportResult {
  const hasCSSObject = typeof CSS !== 'undefined';
  const hasCSSSupports = hasCSSObject && typeof CSS.supports === 'function';

  const checks: CssFeatureCheck[] = [
    {
      feature: 'color-mix',
      supported:
        hasCSSSupports &&
        CSS.supports('color', 'color-mix(in srgb, red 50%, blue)'),
    },
    {
      feature: '@property',
      supported: hasCSSObject && typeof CSS.registerProperty === 'function',
    },
  ];

  const unsupportedFeatures = checks
    .filter((c) => !c.supported)
    .map((c) => c.feature);

  return { supported: unsupportedFeatures.length === 0, unsupportedFeatures };
}

/**
 * Checks if the current browser supports all required features.
 *
 * Returns the full aggregated result, including which features are unsupported.
 */
export function isBrowserSupported(): BrowserSupportResult {
  const tw4 = checkTailwind4Support();

  return {
    supported: tw4.supported,
    unsupportedFeatures: tw4.unsupportedFeatures,
  };
}
